import { useState, useEffect, useRef } from 'react';
import { bookmarkService } from '../../services/bookmarks';
import { t } from '../../i18n/uiText';

export default function AddBookmarkDialog({ bookId, chapter, verse, bookName, onClose, onBookmarkAdded }) {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const id = setTimeout(() => {
      try {
        inputRef.current?.focus({ preventScroll: true });
      } catch {
        inputRef.current?.focus();
      }
    }, 60);
    return () => clearTimeout(id);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    try {
      setLoading(true);
      setError(null);

      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        setError(t('bookmarkAuthRequired'));
        return;
      }

      const verseValue = verse ? parseInt(verse) : null;
      const chapterValue = chapter ? parseInt(chapter) : null;

      await bookmarkService.addBookmark(bookId, chapterValue, verseValue, note || null);

      if (onBookmarkAdded) {
        await onBookmarkAdded();
      }

      onClose();
    } catch (err) {
      console.error('Error adding bookmark:', err);
      setError(err.message || t('bookmarkAddError'));
    } finally {
      setLoading(false);
    }
  };

  const getBookmarkTitle = () => {
    if (verse) {
      return `${bookName} ${chapter}:${verse}`;
    } else if (chapter) {
      return `${bookName} ${chapter}`;
    }
    return bookName;
  };

  return (
    <div
      className="bf-bookmark-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t('addBookmarkTitle')}
    >
      <div className="bf-bookmark-card" onClick={(e) => e.stopPropagation()}>
        <div className="bf-bookmark-card__header">
          <h2 className="bf-bookmark-card__title">{t('addBookmarkTitle')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="bf-bookmark-card__close"
            aria-label={t('close')}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="bf-bookmark-card__body">
          <div className="bf-bookmark-loc">
            <span className="bf-bookmark-loc__label">{t('bookmarkLocationLabel')}</span>
            <span className="bf-bookmark-loc__value">{getBookmarkTitle()}</span>
          </div>

          <div className="bf-bookmark-field">
            <label htmlFor="bookmark-note" className="bf-bookmark-field__label">
              {t('bookmarkTitleOptional')}
            </label>
            <input
              ref={inputRef}
              id="bookmark-note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('bookmarkTitlePlaceholder')}
              className="bf-login-card__input"
              maxLength={200}
            />
            <span className="bf-bookmark-field__counter">
              {t('bookmarkTitleCounter').replace('{count}', String(note.length)).replace('{max}', '200')}
            </span>
          </div>

          {error && <div className="bf-bookmark-error">{error}</div>}

          <div className="bf-bookmark-card__footer">
            <button
              type="button"
              onClick={onClose}
              className="bf-login-card__btn-ghost"
              disabled={loading}
            >
              {t('cancelAction')}
            </button>
            <button
              type="submit"
              className="bf-login-card__btn-primary"
              disabled={loading}
            >
              {loading ? t('addingBookmark') : t('addBookmark')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
